import React, { useState } from 'react';
import { Feedback } from '@questlabs/react-sdk';
import SafeIcon from '../common/SafeIcon';

const INTERVIEW_LIST = [
  { id: 1, name: 'Yaw Mensah', role: 'Operations Manager', time: '10:00 AM - 11:00 AM', status: 'Completed', avatar: 'https://i.pravatar.cc/150?img=11', score: 92 },
  { id: 2, name: 'Ama Serwaa', role: 'Frontend Developer', time: '02:00 PM - 03:00 PM', status: 'Completed', avatar: 'https://i.pravatar.cc/150?img=32', score: 88 },
  { id: 3, name: 'Ekow Thompson', role: 'Sales Executive', time: '04:30 PM - 05:30 PM', status: 'In Progress', avatar: 'https://i.pravatar.cc/150?img=12', score: null },
];

const CALENDAR_DAYS = [
  { day: 'Mon', date: '24', current: false, dots: 2 },
  { day: 'Tue', date: '25', current: true, dots: 3 },
  { day: 'Wed', date: '26', current: false, dots: 1 },
  { day: 'Thu', date: '27', current: false, dots: 0 },
  { day: 'Fri', date: '28', current: false, dots: 2 },
];

const InterviewFeedback = () => {
  const [selectedInterview, setSelectedInterview] = useState(INTERVIEW_LIST[0]);

  const styleConfig = {
    Form: { backgroundColor: '#FFFFFF', borderRadius: '12px', padding: '32px', border: '1px solid #f1f5f9', boxShadow: 'none' },
    Heading: { color: '#11469A', fontSize: '22px', fontWeight: '800', marginBottom: '8px' },
    Description: { color: '#64748b', fontSize: '14px', marginBottom: '32px', lineHeight: '1.6' },
    Label: { color: '#475569', fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.025em', marginBottom: '10px' },
    Input: { border: '1px solid #e2e8f0', borderRadius: '8px', height: '48px', fontSize: '14px' },
    TextArea: { border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', minHeight: '120px' },
    PrimaryButton: { backgroundColor: '#11469A', color: '#FFFFFF', borderRadius: '8px', fontWeight: '700', fontSize: '14px', height: '52px', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '16px' },
    SingleChoice: { style: { backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', color: '#4b5563' }, selectedStyle: { backgroundColor: '#11469A', color: '#FFFFFF' }, hoverBackground: '#f1f5f9' },
    listHover: { background: '#f8fafc', iconBackground: '#11469A', iconColor: '#FFFFFF', Heading: '#11469A', Description: '#64748b', Icon: { color: '#64748b' }, defaultIconBackground: '#f1f5f9' }
  };

  // Comprehensive mock data to ensure the SDK form displays properly populated
  const mockFormData = {
    "Technical Assessment": "4",
    "Communication Skills": "5",
    "Cultural Fit": "Excellent",
    "Recommendation": "Strong Hire",
    "Detailed Notes": "Yaw demonstrated exceptional understanding of supply chain logistics in the Ghanaian context. His previous work provides him with the necessary background to hit the ground running. Highly recommended for the Operations Manager role."
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Interview Feedback</h1>
          <p className="text-gray-500">Document post-interview evaluations and recommendations.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 space-y-4">
          <div className="card p-6">
            <div className="flex justify-between items-center mb-6">
              <h4 className="font-bold text-gray-800 text-sm uppercase tracking-widest">Upcoming Calendar</h4>
              <span className="text-xs text-[#11469A] font-bold">Oct 2023</span>
            </div>
            <div className="flex justify-between items-center mb-6">
              {CALENDAR_DAYS.map((cal, i) => (
                <div key={i} className={`flex flex-col items-center gap-1 group cursor-pointer`}>
                  <span className="text-[10px] font-bold text-gray-400 uppercase">{cal.day}</span>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold transition-all ${cal.current ? 'bg-[#11469A] text-white shadow-md' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}>
                    {cal.date}
                  </div>
                  <div className="flex gap-0.5">
                    {[...Array(cal.dots)].map((_, j) => (
                      <div key={j} className="w-1 h-1 rounded-full bg-[#11469A]" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-3">
              {INTERVIEW_LIST.map((interview) => (
                <button 
                  key={interview.id} 
                  onClick={() => setSelectedInterview(interview)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${selectedInterview.id === interview.id ? 'bg-blue-50/50 border border-blue-100 shadow-sm' : 'hover:bg-gray-50 border border-transparent'}`}
                >
                  <img src={interview.avatar} className="w-10 h-10 rounded-full object-cover" alt="" />
                  <div className="text-left flex-1">
                    <h5 className="text-xs font-bold text-gray-800">{interview.name}</h5>
                    <p className="text-[10px] text-gray-400 font-medium">{interview.time}</p>
                  </div>
                  {interview.score && (
                    <span className="text-[10px] font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">{interview.score}%</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="card p-6 bg-brand-dark text-white">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-[#00AEEF]">
                <SafeIcon name="Info" size={20} />
              </div>
              <h4 className="font-bold text-sm">Evaluation Policy</h4>
            </div>
            <p className="text-xs text-white/60 leading-relaxed font-medium">
              Please submit feedback within 24 hours of the session. All scores must be supported by specific examples to ensure objectivity.
            </p>
          </div>
        </div>

        <div className="lg:col-span-8">
          <div className="card p-6 mb-6 flex items-center justify-between border-none shadow-sm">
            <div className="flex items-center gap-4">
              <img src={selectedInterview.avatar} className="w-16 h-16 rounded-2xl shadow-md" alt="" />
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Evaluating Candidate</p>
                <h3 className="text-xl font-bold text-gray-800">{selectedInterview.name}</h3>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Target Position</p>
              <h4 className="text-sm font-bold text-[#11469A]">{selectedInterview.role}</h4>
            </div>
          </div>
          <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
            <Feedback 
              styleConfig={styleConfig} 
              questId="interview-feedback-quest" 
              userId="admin-interviewer" 
              data={mockFormData} 
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewFeedback;